import type { ReactElement, ReactNode } from "react";
import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import type {
  AgentFlowAccountDTO,
  AgentFlowActionWebhookConfigDTO,
  AgentFlowHandoffProvider,
  AgentFlowLlmProvider,
  Tenant,
  UpdateAgentFlowAccountRequest,
  UpdateConnectionHandoffRequest,
  WorkspaceHandoffDTO,
} from "@tw-pulse/shared";
import { api, ApiClientError } from "@/lib/api";
import { Button } from "@/components/ui/Button";
import { Field } from "@/components/ui/Field";

/**
 * Workspace → Settings.
 *
 * Every setting here is tenant/workspace-wide. Two-pane settings layout: a
 * sticky tab rail on the left and a single active panel on the right. Only the
 * active panel mounts, so each concern fetches its own config lazily (and
 * refetches fresh when re-selected). The active tab is mirrored to the `?tab=`
 * query param so views are deep-linkable and survive back/forward + reload.
 *
 * Handoff routing lives here too; the rest (Branding, LLM, Action events,
 * Compliance, Integrations) were relocated from the former AgentFlow → Settings
 * page — all of it is workspace-wide configuration.
 */

type TabId =
  | "handoff"
  | "branding"
  | "llm"
  | "action-events"
  | "compliance"
  | "voice-disclosure"
  | "integrations";

interface TabDef {
  id: TabId;
  label: string;
  caption: string;
  icon: ReactNode;
  Panel: () => ReactElement;
  /**
   * When true the tab is not rendered in the nav and is not reachable via the
   * `?tab=` param. The entry stays in TABS so its Panel + backend remain wired
   * (LLM provider/model is single-provider per ADR 0028 — set at the DB level,
   * not in the UI). Flip to re-expose without re-plumbing.
   */
  hidden?: boolean;
}

const TABS: TabDef[] = [
  {
    id: "handoff",
    label: "Handoff",
    caption: "Where escalations route",
    icon: handoffIcon(),
    Panel: HandoffPanel,
  },
  {
    id: "branding",
    label: "Branding",
    caption: "Name shown in agent prompts",
    icon: tagIcon(),
    Panel: BrandingPanel,
  },
  {
    id: "llm",
    label: "LLM",
    caption: "Model, key, and spend ceiling",
    icon: chipIcon(),
    Panel: LlmPanel,
    // Hidden — single-provider (Anthropic/Haiku) set at the DB level. Panel +
    // updateAccount backend retained; see ADR 0028.
    hidden: true,
  },
  {
    id: "action-events",
    label: "Webhook events",
    caption: "Outbound event webhook (tool calls + goals)",
    icon: webhookIcon(),
    Panel: ActionEventsPanel,
  },
  {
    id: "compliance",
    label: "Compliance",
    caption: "TCPA consent state",
    icon: shieldIcon(),
    Panel: CompliancePanel,
  },
  {
    id: "voice-disclosure",
    label: "Voice disclosure",
    caption: "AI + recording notice spoken on inbound calls",
    icon: shieldIcon(),
    Panel: VoiceDisclosurePanel,
  },
  {
    id: "integrations",
    label: "Integrations",
    caption: "Connected external services",
    icon: plugIcon(),
    Panel: IntegrationsPanel,
  },
];

/** Tabs actually shown in the nav (excludes `hidden` entries). */
const VISIBLE_TABS = TABS.filter((t) => !t.hidden);

export function WorkspaceSettingsPage() {
  const [params, setParams] = useSearchParams();
  const railRef = useRef<HTMLDivElement>(null);

  // Resolve the active tab from the URL; tolerate the legacy `#integrations`
  // anchor and unknown ids by falling back to the first tab.
  const requested = params.get("tab");
  const fromHash = typeof window !== "undefined" && window.location.hash === "#integrations";
  const active: TabId =
    VISIBLE_TABS.find((t) => t.id === requested)?.id ??
    (fromHash ? "integrations" : VISIBLE_TABS[0]!.id);

  function select(id: TabId) {
    const next = new URLSearchParams(params);
    next.set("tab", id);
    setParams(next, { replace: false });
  }

  function onRailKeyDown(e: React.KeyboardEvent, index: number) {
    let nextIndex: number | null = null;
    if (e.key === "ArrowDown" || e.key === "ArrowRight")
      nextIndex = (index + 1) % VISIBLE_TABS.length;
    else if (e.key === "ArrowUp" || e.key === "ArrowLeft")
      nextIndex = (index - 1 + VISIBLE_TABS.length) % VISIBLE_TABS.length;
    else if (e.key === "Home") nextIndex = 0;
    else if (e.key === "End") nextIndex = VISIBLE_TABS.length - 1;
    if (nextIndex === null) return;
    e.preventDefault();
    const tab = VISIBLE_TABS[nextIndex]!;
    select(tab.id);
    const btns = railRef.current?.querySelectorAll<HTMLButtonElement>("[role=tab]");
    btns?.[nextIndex]?.focus();
  }

  const ActivePanel = VISIBLE_TABS.find((t) => t.id === active)!.Panel;

  return (
    <div className="space-y-6">
      <header>
        <p className="text-[11.5px] font-semibold uppercase tracking-[0.16em] text-brand-600 dark:text-brand-400">
          Workspace
        </p>
        <h1 className="mt-1 text-2xl font-semibold tracking-tight text-zinc-900 dark:text-zinc-50">
          Settings
        </h1>
        <p className="mt-1 max-w-2xl text-[13px] text-zinc-500">
          Workspace-wide configuration: where escalations route, how your agent talks, what it can
          spend, and what it&rsquo;s connected to. These apply to the whole workspace.
        </p>
      </header>

      <div className="flex flex-col gap-6 lg:flex-row lg:items-start">
        {/* Tab rail */}
        <div
          ref={railRef}
          role="tablist"
          aria-label="Settings sections"
          aria-orientation="vertical"
          className="-mx-1 flex gap-1 overflow-x-auto px-1 pb-1 lg:sticky lg:top-6 lg:mx-0 lg:w-56 lg:shrink-0 lg:flex-col lg:overflow-visible lg:px-0 lg:pb-0"
        >
          {VISIBLE_TABS.map((t, i) => {
            const isActive = t.id === active;
            return (
              <button
                key={t.id}
                role="tab"
                id={`tab-${t.id}`}
                aria-selected={isActive}
                aria-controls={`panel-${t.id}`}
                tabIndex={isActive ? 0 : -1}
                onClick={() => select(t.id)}
                onKeyDown={(e) => onRailKeyDown(e, i)}
                className={
                  "group relative flex shrink-0 items-center gap-2.5 rounded-lg px-3 py-2 text-left transition " +
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-brand-500 " +
                  "focus-visible:ring-offset-white dark:focus-visible:ring-offset-zinc-950 " +
                  (isActive
                    ? "bg-brand-50 text-brand-700 dark:bg-brand-500/10 dark:text-brand-200"
                    : "text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900 dark:text-zinc-400 dark:hover:bg-zinc-800/50 dark:hover:text-zinc-100")
                }
              >
                <span
                  aria-hidden="true"
                  className={
                    "hidden lg:block lg:absolute lg:inset-y-1.5 lg:left-0 lg:w-0.5 lg:rounded-full " +
                    (isActive ? "lg:bg-brand-500" : "lg:bg-transparent")
                  }
                />
                <span
                  className={
                    isActive
                      ? "text-brand-600 dark:text-brand-300"
                      : "text-zinc-400 dark:text-zinc-500"
                  }
                >
                  {t.icon}
                </span>
                <span className="min-w-0">
                  <span className="block text-[13.5px] font-medium leading-tight">{t.label}</span>
                  <span className="hidden truncate text-[11.5px] text-zinc-500 lg:block">
                    {t.caption}
                  </span>
                </span>
              </button>
            );
          })}
        </div>

        {/* Active panel */}
        <div
          role="tabpanel"
          id={`panel-${active}`}
          aria-labelledby={`tab-${active}`}
          tabIndex={0}
          className="min-w-0 flex-1 focus-visible:outline-none"
        >
          <ActivePanel key={active} />
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Shared panel chrome + form primitives
// ---------------------------------------------------------------------------

const labelCls = "mb-1.5 block text-[12px] font-medium text-zinc-700 dark:text-zinc-300";
const inputShell =
  "block w-full rounded-md border bg-white px-3 py-2 text-zinc-900 placeholder:text-zinc-400 " +
  "outline-none transition border-zinc-200 hover:border-zinc-300 focus-visible:border-brand-500 focus-visible:shadow-ring " +
  "disabled:cursor-not-allowed disabled:bg-zinc-50 disabled:text-zinc-500 " +
  "dark:border-zinc-800 dark:bg-zinc-950/40 dark:text-zinc-100 dark:placeholder:text-zinc-600 dark:hover:border-zinc-700 " +
  "dark:focus-visible:border-brand-500/70 dark:disabled:bg-zinc-900/40";
const textInputCls = inputShell + " text-[14px]";
const monoInputCls = inputShell + " font-mono text-[12.5px]";
const selectCls = inputShell + " text-[14px]";

function PanelCard({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children: ReactNode;
}) {
  return (
    <section className="rounded-xl border border-zinc-200 bg-white shadow-card-light dark:border-zinc-800/80 dark:bg-zinc-900/40 dark:shadow-none">
      <header className="border-b border-zinc-200/80 px-5 py-4 dark:border-zinc-800/60">
        <h2 className="text-[15px] font-semibold text-zinc-900 dark:text-zinc-100">{title}</h2>
        {subtitle && (
          <p className="mt-1 max-w-2xl text-[12.5px] leading-relaxed text-zinc-500">{subtitle}</p>
        )}
      </header>
      <div className="px-5 py-5">{children}</div>
    </section>
  );
}

function SubCard({
  title,
  configured,
  children,
}: {
  title: string;
  configured?: boolean;
  children: ReactNode;
}) {
  return (
    <div className="rounded-lg border border-zinc-200 p-4 dark:border-zinc-800/80">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-[13.5px] font-medium text-zinc-900 dark:text-zinc-100">{title}</h3>
        {configured && <ConfiguredBadge />}
      </div>
      {children}
    </div>
  );
}

function SaveBar({ children }: { children: ReactNode }) {
  return (
    <div className="mt-5 flex flex-wrap items-center gap-3 border-t border-zinc-200/70 pt-4 dark:border-zinc-800/60">
      {children}
    </div>
  );
}

function Saved() {
  return (
    <span className="inline-flex items-center gap-1 text-[12px] font-medium text-emerald-600 dark:text-emerald-400">
      <svg viewBox="0 0 16 16" width="13" height="13" fill="none" aria-hidden="true">
        <path
          d="m3.5 8.5 3 3 6-7"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      Saved
    </span>
  );
}

function ErrText({ children }: { children: ReactNode }) {
  return (
    <span role="alert" className="text-[12px] text-red-600 dark:text-red-400">
      {children}
    </span>
  );
}

function ConfiguredBadge() {
  return (
    <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[10.5px] font-medium text-emerald-700 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-300">
      <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" aria-hidden="true" />
      Configured
    </span>
  );
}

function PanelSkeleton({ title }: { title: string }) {
  return (
    <PanelCard title={title}>
      <div className="space-y-3">
        <div className="h-9 animate-pulse rounded-md bg-zinc-100 dark:bg-zinc-800/40" />
        <div className="h-9 w-2/3 animate-pulse rounded-md bg-zinc-100 dark:bg-zinc-800/40" />
      </div>
    </PanelCard>
  );
}

function NotActivatedNotice() {
  return (
    <p className="rounded-md border border-zinc-200 bg-zinc-50 px-3 py-2.5 text-[12.5px] text-zinc-600 dark:border-zinc-800/80 dark:bg-zinc-900/40 dark:text-zinc-400">
      Activate AgentFlow on the Flows page to configure these settings.
    </p>
  );
}

// ---------------------------------------------------------------------------
// Handoff (escalation routing — default + per-connection overrides)
// ---------------------------------------------------------------------------

// Common shape shared by the default config and per-connection overrides.
interface HandoffValue {
  provider: AgentFlowHandoffProvider;
  twilioFlex: { configured: boolean; workspaceSid: string | null; workflowSid: string | null };
  webhook: { configured: boolean; url: string | null; signingSecretOnFile: boolean };
}

const handoffSelectCls =
  "h-9 rounded-md border border-zinc-200 bg-white px-2.5 text-[13.5px] text-zinc-800 outline-none transition " +
  "hover:border-zinc-300 focus-visible:border-brand-500 focus-visible:shadow-ring dark:border-zinc-800 dark:bg-zinc-950/40 dark:text-zinc-200 dark:hover:border-zinc-700";

function HandoffPanel() {
  type LoadState =
    | { status: "loading" }
    | { status: "ready"; data: WorkspaceHandoffDTO }
    | { status: "error"; message: string };

  const [state, setState] = useState<LoadState>({ status: "loading" });

  async function load() {
    try {
      setState({ status: "ready", data: await api.workspace.getHandoff() });
    } catch (e) {
      setState({
        status: "error",
        message: e instanceof ApiClientError ? e.message : "Failed to load settings",
      });
    }
  }

  useEffect(() => {
    void load();
  }, []);

  if (state.status === "loading") return <PanelSkeleton title="Handoff" />;
  if (state.status === "error") {
    return (
      <PanelCard title="Handoff">
        <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2.5 text-[13px] text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300">
          {state.message}
        </p>
      </PanelCard>
    );
  }

  return (
    <div className="space-y-5">
      <HandoffCard
        title="Default handoff"
        subtitle="Where escalations route. Used by AgentFlow conversations and any account without an override. A Twilio Flex Workspace lives inside one account, so per-account overrides are configured below."
      >
        <HandoffForm
          value={state.data.default}
          inheritLabel="Off — log only"
          onSave={async (body) => {
            await api.workspace.updateDefaultHandoff(body);
            await load();
          }}
        />
      </HandoffCard>

      {state.data.connections.length === 0 ? (
        <p className="text-[12.5px] text-zinc-500">
          No connected Twilio accounts yet. Connect one to configure a per-account handoff.
        </p>
      ) : (
        state.data.connections.map((c) => (
          <HandoffCard
            key={c.connectionId}
            title={c.connectionName}
            subtitle="Per-account override"
          >
            <HandoffForm
              value={c}
              inheritLabel="Inherit workspace default"
              onSave={async (body) => {
                await api.workspace.updateConnectionHandoff(c.connectionId, body);
                await load();
              }}
            />
          </HandoffCard>
        ))
      )}
    </div>
  );
}

function HandoffCard({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle: string;
  children: ReactNode;
}) {
  return (
    <section className="rounded-xl border border-zinc-200 bg-white shadow-card-light dark:border-zinc-800/80 dark:bg-zinc-900/40 dark:shadow-none">
      <header className="border-b border-zinc-200/80 px-5 py-3.5 dark:border-zinc-800/60">
        <h2 className="text-[14px] font-semibold text-zinc-900 dark:text-zinc-100">{title}</h2>
        <p className="mt-0.5 max-w-2xl text-[12px] leading-relaxed text-zinc-500">{subtitle}</p>
      </header>
      <div className="px-5 py-4">{children}</div>
    </section>
  );
}

/** Provider selector + the selected provider's config + save. */
function HandoffForm({
  value,
  inheritLabel,
  onSave,
}: {
  value: HandoffValue;
  /** Label for the "none" option (default = "off", per-connection = "inherit"). */
  inheritLabel: string;
  onSave: (body: UpdateConnectionHandoffRequest) => Promise<void>;
}) {
  const [provider, setProvider] = useState<AgentFlowHandoffProvider>(value.provider);
  const [workspaceSid, setWorkspaceSid] = useState(value.twilioFlex.workspaceSid ?? "");
  const [workflowSid, setWorkflowSid] = useState(value.twilioFlex.workflowSid ?? "");
  const [webhookUrl, setWebhookUrl] = useState(value.webhook.url ?? "");
  const [webhookSecret, setWebhookSecret] = useState("");
  const [busy, setBusy] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [err, setErr] = useState<string | null>(null);

  async function save() {
    setBusy(true);
    setErr(null);
    try {
      const body: UpdateConnectionHandoffRequest = { provider };
      if (provider === "twilio_flex") {
        if (!workspaceSid.trim() || !workflowSid.trim()) {
          throw new Error("Workspace SID and Workflow SID are both required.");
        }
        body.twilioFlex = { workspaceSid: workspaceSid.trim(), workflowSid: workflowSid.trim() };
      } else if (provider === "handoff_webhook") {
        if (!webhookUrl.trim()) throw new Error("Webhook URL is required.");
        const w: { url: string; signingSecret?: string } = { url: webhookUrl.trim() };
        if (webhookSecret.trim()) w.signingSecret = webhookSecret.trim();
        body.webhook = w;
      }
      await onSave(body);
      setWebhookSecret("");
      setSavedAt(Date.now());
    } catch (e) {
      setErr(e instanceof ApiClientError || e instanceof Error ? e.message : "Save failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        void save();
      }}
    >
      <label className="block max-w-md">
        <span className={labelCls}>Provider</span>
        <select
          value={provider}
          onChange={(e) => {
            setProvider(e.target.value as AgentFlowHandoffProvider);
            setSavedAt(null);
          }}
          className={handoffSelectCls + " w-full"}
        >
          <option value="none">{inheritLabel}</option>
          <option value="twilio_flex">Twilio Flex (TaskRouter)</option>
          <option value="handoff_webhook">Webhook (any third party)</option>
        </select>
      </label>

      {provider === "twilio_flex" && (
        <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2">
          <label className="block">
            <span className={labelCls}>Workspace SID</span>
            <input
              value={workspaceSid}
              onChange={(e) => setWorkspaceSid(e.target.value)}
              placeholder="WS..."
              className={monoInputCls}
            />
          </label>
          <label className="block">
            <span className={labelCls}>Workflow SID</span>
            <input
              value={workflowSid}
              onChange={(e) => setWorkflowSid(e.target.value)}
              placeholder="WW..."
              className={monoInputCls}
            />
          </label>
        </div>
      )}

      {provider === "handoff_webhook" && (
        <div className="mt-4 space-y-3">
          <label className="block">
            <span className={labelCls}>URL</span>
            <input
              value={webhookUrl}
              onChange={(e) => setWebhookUrl(e.target.value)}
              placeholder="https://your-system.example.com/handoff"
              className={monoInputCls}
            />
          </label>
          <label className="block">
            <span className={labelCls}>
              Signing secret{" "}
              {value.webhook.signingSecretOnFile && (
                <span className="font-normal text-zinc-400">(on file — leave blank to keep)</span>
              )}
            </span>
            <input
              type="password"
              value={webhookSecret}
              onChange={(e) => setWebhookSecret(e.target.value)}
              placeholder={value.webhook.signingSecretOnFile ? "Leave blank to keep" : "≥16 chars"}
              className={monoInputCls}
            />
          </label>
        </div>
      )}

      <SaveBar>
        <Button type="submit" disabled={busy}>
          {busy ? "Saving…" : "Save"}
        </Button>
        {savedAt && <Saved />}
        {err && <ErrText>{err}</ErrText>}
      </SaveBar>
    </form>
  );
}

// ---------------------------------------------------------------------------
// Branding
// ---------------------------------------------------------------------------

function BrandingPanel() {
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    void api.me().then((me) => {
      setTenant(me.tenant);
      setValue(me.tenant.businessName ?? "");
    });
  }, []);

  async function save() {
    setBusy(true);
    setErr(null);
    try {
      const next = await api.tenants.updateMe({
        businessName: value.trim() === "" ? null : value.trim(),
      });
      setTenant(next);
      setSavedAt(Date.now());
    } catch (e) {
      setErr(e instanceof ApiClientError ? e.message : "Save failed");
    } finally {
      setBusy(false);
    }
  }

  if (!tenant) return <PanelSkeleton title="Branding" />;

  return (
    <PanelCard title="Branding" subtitle="Substituted for {businessName} in compiled flow prompts.">
      <form
        className="max-w-md"
        onSubmit={(e) => {
          e.preventDefault();
          void save();
        }}
      >
        <Field
          label="Business name"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder={tenant.name}
          hint={`Leave blank to fall back to the tenant's name (${tenant.name}).`}
        />
        <SaveBar>
          <Button type="submit" disabled={busy}>
            {busy ? "Saving…" : "Save"}
          </Button>
          {savedAt && <Saved />}
          {err && <ErrText>{err}</ErrText>}
        </SaveBar>
      </form>
    </PanelCard>
  );
}

// ---------------------------------------------------------------------------
// LLM
// ---------------------------------------------------------------------------

function LlmPanel() {
  const [account, setAccount] = useState<AgentFlowAccountDTO | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [draft, setDraft] = useState<UpdateAgentFlowAccountRequest>({});
  const [apiKeyInput, setApiKeyInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    void api.agentflow
      .getAccount()
      .then(setAccount)
      .catch(() => setAccount(null))
      .finally(() => setLoaded(true));
  }, []);

  async function save() {
    setBusy(true);
    setErr(null);
    try {
      const payload: UpdateAgentFlowAccountRequest = { ...draft };
      if (apiKeyInput.trim()) payload.apiKey = apiKeyInput.trim();
      const next = await api.agentflow.updateAccount(payload);
      setAccount(next);
      setDraft({});
      setApiKeyInput("");
      setSavedAt(Date.now());
    } catch (e) {
      setErr(e instanceof ApiClientError ? e.message : "Save failed");
    } finally {
      setBusy(false);
    }
  }

  if (!loaded) return <PanelSkeleton title="LLM" />;

  if (!account) {
    return (
      <PanelCard title="LLM" subtitle="Provider, model, BYOK key, and monthly cost ceiling.">
        <NotActivatedNotice />
      </PanelCard>
    );
  }

  const provider = draft.provider ?? account.llm.provider;
  const model = draft.model ?? account.llm.model;
  const byok = draft.byok ?? account.llm.byok;
  const ceiling =
    draft.monthlyCostCeilingUSD !== undefined
      ? draft.monthlyCostCeilingUSD
      : account.monthlyCostCeilingUSD;

  return (
    <PanelCard title="LLM" subtitle="Provider, model, BYOK key, and monthly cost ceiling.">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void save();
        }}
      >
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <label className="block">
            <span className={labelCls}>Provider</span>
            <select
              value={provider}
              onChange={(e) =>
                setDraft({ ...draft, provider: e.target.value as AgentFlowLlmProvider })
              }
              className={selectCls}
            >
              <option value="anthropic">Anthropic</option>
              <option value="openai">OpenAI</option>
              <option value="google">Google</option>
            </select>
          </label>
          <Field
            label="Model"
            value={model}
            onChange={(e) => setDraft({ ...draft, model: e.target.value })}
          />
        </div>

        <label className="mt-4 flex items-center gap-2 text-[13.5px] text-zinc-800 dark:text-zinc-200">
          <input
            type="checkbox"
            checked={byok}
            onChange={(e) => setDraft({ ...draft, byok: e.target.checked })}
            className="h-4 w-4 rounded border-zinc-300 text-brand-600 focus:ring-brand-500 dark:border-zinc-700 dark:bg-zinc-950/40"
          />
          Bring your own key
        </label>
        {byok && (
          <label className="mt-2 block max-w-md">
            <span className={labelCls}>
              API key{" "}
              {account.llm.apiKeyOnFile && (
                <span className="font-normal text-zinc-400">(currently on file)</span>
              )}
            </span>
            <input
              type="password"
              value={apiKeyInput}
              onChange={(e) => setApiKeyInput(e.target.value)}
              placeholder={account.llm.apiKeyOnFile ? "Leave blank to keep existing" : "sk-…"}
              className={monoInputCls}
            />
          </label>
        )}

        <label className="mt-4 block max-w-md">
          <span className={labelCls}>Monthly cost ceiling (USD)</span>
          <input
            type="number"
            min={0}
            step={1}
            value={ceiling ?? ""}
            onChange={(e) =>
              setDraft({
                ...draft,
                monthlyCostCeilingUSD: e.target.value === "" ? null : Number(e.target.value),
              })
            }
            placeholder="No ceiling"
            className={textInputCls}
          />
          <span className="mt-1.5 block text-[11.5px] text-zinc-500">
            Soft alert only — we log a warning when this month&rsquo;s spend crosses the threshold.
            No hard cutoff.
          </span>
        </label>

        <SaveBar>
          <Button type="submit" disabled={busy}>
            {busy ? "Saving…" : "Save"}
          </Button>
          {savedAt && <Saved />}
          {err && <ErrText>{err}</ErrText>}
        </SaveBar>
      </form>
    </PanelCard>
  );
}

// ---------------------------------------------------------------------------
// Action events
// ---------------------------------------------------------------------------

function ActionEventsPanel() {
  const [config, setConfig] = useState<AgentFlowActionWebhookConfigDTO | null>(null);
  const [enabled, setEnabled] = useState(false);
  const [webhookUrl, setWebhookUrl] = useState("");
  const [webhookSecret, setWebhookSecret] = useState("");
  const [rotateSecret, setRotateSecret] = useState(false);
  const [busy, setBusy] = useState(false);
  const [testBusy, setTestBusy] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [testMsg, setTestMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [err, setErr] = useState<string | null>(null);

  function hydrate(c: AgentFlowActionWebhookConfigDTO) {
    setConfig(c);
    setEnabled(c.enabled);
    setWebhookUrl(c.webhook.url ?? "");
    setWebhookSecret("");
    setRotateSecret(false);
  }

  useEffect(() => {
    void api.agentflow
      .getActionWebhook()
      .then(hydrate)
      .catch(() => setConfig(null));
  }, []);

  if (!config) return <PanelSkeleton title="Webhook events" />;

  const webhookDirty =
    webhookUrl !== (config.webhook.url ?? "") ||
    (rotateSecret && webhookSecret.length > 0) ||
    (!config.webhook.signingSecretOnFile && webhookSecret.length > 0);
  const enabledDirty = enabled !== config.enabled;
  const anyDirty = webhookDirty || enabledDirty;

  async function save() {
    setBusy(true);
    setErr(null);
    setTestMsg(null);
    try {
      const body: Parameters<typeof api.agentflow.updateActionWebhook>[0] = {};
      if (enabledDirty) body.enabled = enabled;
      if (webhookDirty) {
        if (webhookUrl.trim()) {
          const w: { url: string; signingSecret?: string } = { url: webhookUrl.trim() };
          if (webhookSecret.trim()) w.signingSecret = webhookSecret.trim();
          body.webhook = w;
        } else {
          body.webhook = null;
        }
      }
      if (Object.keys(body).length === 0) {
        setBusy(false);
        return;
      }
      const next = await api.agentflow.updateActionWebhook(body);
      hydrate(next);
      setSavedAt(Date.now());
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Save failed");
    } finally {
      setBusy(false);
    }
  }

  async function sendTest() {
    setTestBusy(true);
    setErr(null);
    setTestMsg(null);
    try {
      const res = await api.agentflow.sendTestActionEvent();
      setTestMsg(
        res.ok
          ? { ok: true, text: `Delivered${res.status ? ` (HTTP ${res.status})` : ""}.` }
          : {
              ok: false,
              text: `Endpoint did not accept the event${res.status ? ` (HTTP ${res.status})` : ""}.`,
            },
      );
    } catch (e) {
      setTestMsg({ ok: false, text: e instanceof Error ? e.message : "Test failed" });
    } finally {
      setTestBusy(false);
    }
  }

  return (
    <PanelCard
      title="Webhook events"
      subtitle="Set the destination, then opt in per tool ('Emit event for this tool') and per goal. Delivers agentflow.tool_call + agentflow.goal_reached. Wire it to a CRM, queue, or automation."
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void save();
        }}
      >
        <label className="mb-4 flex items-start gap-3">
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
            className="mt-0.5 h-4 w-4 rounded border-zinc-300 text-brand-600 focus:ring-brand-500 dark:border-zinc-700 dark:bg-zinc-950/40"
          />
          <span className="flex flex-col">
            <span className="text-[13.5px] font-medium text-zinc-900 dark:text-zinc-100">
              Emit webhook events
            </span>
            <span className="text-[11.5px] text-zinc-500">
              Master switch. Only tools and goals you opt in (via the canvas checkbox) emit; failed
              tool calls don't. Sandbox runs DO emit (marked channel: "sandbox") so you can test.
            </span>
          </span>
        </label>

        {enabled && !config.webhook.configured && !webhookUrl.trim() && (
          <p className="mb-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-[11.5px] text-amber-700 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300">
            Emission is on but no endpoint is configured — events won&rsquo;t be delivered until you
            add a URL below.
          </p>
        )}

        <SubCard title="Endpoint" configured={config.webhook.configured}>
          <label className="block">
            <span className={labelCls}>URL</span>
            <input
              value={webhookUrl}
              onChange={(e) => setWebhookUrl(e.target.value)}
              placeholder="https://your-system.example.com/agentflow/events"
              className={monoInputCls}
            />
          </label>
          <label className="mt-3 block">
            <span className={labelCls}>
              Signing secret{" "}
              {config.webhook.signingSecretOnFile && (
                <span className="font-normal text-zinc-400">(on file)</span>
              )}
            </span>
            <input
              type="password"
              value={webhookSecret}
              onChange={(e) => setWebhookSecret(e.target.value)}
              placeholder={
                config.webhook.signingSecretOnFile
                  ? rotateSecret
                    ? "Enter a new secret (≥16 chars)"
                    : "Leave blank to keep existing"
                  : "Required — ≥16 chars"
              }
              disabled={config.webhook.signingSecretOnFile && !rotateSecret}
              className={monoInputCls}
            />
            {config.webhook.signingSecretOnFile && !rotateSecret && (
              <button
                type="button"
                onClick={() => setRotateSecret(true)}
                className="mt-1.5 text-[11.5px] font-medium text-brand-600 hover:underline dark:text-brand-300"
              >
                Rotate signing secret
              </button>
            )}
          </label>
          <p className="mt-3 text-[11.5px] leading-relaxed text-zinc-500">
            We POST{" "}
            <code className="rounded bg-zinc-100 px-1 py-0.5 font-mono text-[10.5px] dark:bg-zinc-800/70">
              {"{ event, type, tenantId, flowId, conversationId, channel, data, emittedAt }"}
            </code>{" "}
            where <code className="font-mono text-[11px]">type</code> is the action and{" "}
            <code className="font-mono text-[11px]">data</code> carries the input the agent
            collected plus the outcome. Body is signed with HMAC-SHA256:{" "}
            <code className="rounded bg-zinc-100 px-1 py-0.5 font-mono text-[10.5px] dark:bg-zinc-800/70">
              X-TwilioPulse-Signature: t=&lt;ms&gt;,v1=&lt;hex&gt;
            </code>
            .
          </p>
        </SubCard>

        <SaveBar>
          <Button type="submit" disabled={busy || !anyDirty}>
            {busy ? "Saving…" : "Save"}
          </Button>
          <Button
            type="button"
            variant="secondary"
            onClick={() => void sendTest()}
            disabled={testBusy || !config.webhook.configured || webhookDirty}
          >
            {testBusy ? "Sending…" : "Send test event"}
          </Button>
          {savedAt && <Saved />}
          {webhookDirty && config.webhook.configured && (
            <span className="text-[11.5px] text-zinc-500">Save to test changes.</span>
          )}
          {testMsg && (
            <span
              className={`text-[12px] ${testMsg.ok ? "text-emerald-600 dark:text-emerald-400" : "text-red-600 dark:text-red-400"}`}
            >
              {testMsg.text}
            </span>
          )}
          {err && <ErrText>{err}</ErrText>}
        </SaveBar>
      </form>
    </PanelCard>
  );
}

// ---------------------------------------------------------------------------
// Voice disclosure
// ---------------------------------------------------------------------------

/**
 * Per-tenant AI/recording notice spoken at the start of every inbound voice
 * call, before the flow's greeting.
 *
 * Three states, and the difference is legal rather than cosmetic:
 *   inherit  (null) — track the platform default; if counsel changes it, this
 *                     tenant follows automatically.
 *   custom   (text) — this tenant's own wording.
 *   disabled ("")   — off for this tenant.
 *
 * Modelled as an explicit radio rather than "empty means off", so a tenant that
 * clears the box to type new wording can't silently disable a compliance notice.
 */
function VoiceDisclosurePanel() {
  const [account, setAccount] = useState<AgentFlowAccountDTO | null>(null);
  const [mode, setMode] = useState<"inherit" | "custom" | "disabled">("inherit");
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    void api.agentflow
      .getAccount()
      .then((a) => {
        setAccount(a);
        if (a.voiceDisclosure === null) setMode("inherit");
        else if (a.voiceDisclosure.trim() === "") setMode("disabled");
        else {
          setMode("custom");
          setText(a.voiceDisclosure);
        }
      })
      .catch(() => setAccount(null));
  }, []);

  async function save() {
    setBusy(true);
    setErr(null);
    try {
      const voiceDisclosure = mode === "inherit" ? null : mode === "disabled" ? "" : text.trim();
      if (mode === "custom" && voiceDisclosure === "") {
        setErr("Enter the wording, or choose Disabled to turn the notice off.");
        return;
      }
      const next = await api.agentflow.updateAccount({ voiceDisclosure });
      setAccount(next);
      setSavedAt(Date.now());
    } catch (e) {
      setErr(e instanceof ApiClientError ? e.message : "Save failed");
    } finally {
      setBusy(false);
    }
  }

  if (!account) {
    return (
      <PanelCard title="Voice disclosure" subtitle="Spoken at the start of every inbound call.">
        <p className="text-[13px] text-zinc-500">Activate AgentFlow to configure this.</p>
      </PanelCard>
    );
  }

  return (
    <PanelCard
      title="Voice disclosure"
      subtitle="Spoken before the greeting on every inbound voice call."
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void save();
        }}
      >
        <div className="space-y-2">
          {(
            [
              [
                "inherit",
                "Use the platform default",
                "Follows the default set for this deployment.",
              ],
              ["custom", "Custom wording", "Your own notice, spoken verbatim."],
              ["disabled", "No disclosure", "Nothing is spoken before the greeting."],
            ] as const
          ).map(([value, label, hint]) => (
            <label key={value} className="flex items-start gap-2.5">
              <input
                type="radio"
                name="voice-disclosure-mode"
                checked={mode === value}
                onChange={() => setMode(value)}
                className="mt-1 h-4 w-4 border-zinc-300 text-brand-600 focus:ring-brand-500 dark:border-zinc-700 dark:bg-zinc-950/40"
              />
              <span>
                <span className="block text-[13.5px] text-zinc-800 dark:text-zinc-200">
                  {label}
                </span>
                <span className="block text-[11.5px] text-zinc-500">{hint}</span>
              </span>
            </label>
          ))}
        </div>

        {mode === "custom" && (
          <label className="mt-3 block">
            <span className={labelCls}>Wording</span>
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={2}
              maxLength={500}
              placeholder="Just so you know, you're speaking with an AI assistant, and this call may be recorded."
              className={textInputCls}
            />
          </label>
        )}

        <p className="mt-3 text-[11.5px] text-zinc-500">
          Disclosure duties are jurisdiction-specific — one-party vs all-party consent, and
          AI-disclosure statutes differ by state. Review changes with counsel before turning the
          notice off.
        </p>

        <SaveBar>
          <Button type="submit" disabled={busy}>
            {busy ? "Saving…" : "Save"}
          </Button>
          {savedAt && <Saved />}
          {err && <ErrText>{err}</ErrText>}
        </SaveBar>
      </form>
    </PanelCard>
  );
}

// ---------------------------------------------------------------------------
// Compliance
// ---------------------------------------------------------------------------

function CompliancePanel() {
  const [summary, setSummary] = useState<{
    optedIn: number;
    optedOut: number;
    unknown: number;
  } | null>(null);
  const [phone, setPhone] = useState("");
  const [intent, setIntent] = useState<"opted_in" | "opted_out">("opted_in");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    void api.agentflow
      .consentSummary()
      .then(setSummary)
      .catch(() => setSummary(null));
  }, []);

  async function submit() {
    setBusy(true);
    setErr(null);
    setMsg(null);
    try {
      await api.agentflow.recordConsent({
        endCustomerPhone: phone,
        state: intent,
        source: "manual",
        note: note || undefined,
      });
      setMsg(`Recorded ${intent} for ${phone}`);
      setPhone("");
      setNote("");
      const next = await api.agentflow.consentSummary();
      setSummary(next);
    } catch (e) {
      setErr(e instanceof ApiClientError ? e.message : "Failed to record consent");
    } finally {
      setBusy(false);
    }
  }

  return (
    <PanelCard
      title="Compliance"
      subtitle="TCPA consent state for end customers. STOP / HELP / START are handled automatically on the inbound webhook."
    >
      {summary && (
        <div className="mb-5 grid grid-cols-3 gap-3">
          <Counter label="Opted in" value={summary.optedIn} tone="emerald" />
          <Counter label="Unknown" value={summary.unknown} tone="zinc" />
          <Counter label="Opted out" value={summary.optedOut} tone="red" />
        </div>
      )}
      <form
        className="space-y-3"
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <p className="text-[12px] font-medium text-zinc-700 dark:text-zinc-300">
          Record manual consent
        </p>
        <div className="flex flex-wrap gap-2">
          <input
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="+14155551234"
            className={monoInputCls + " min-w-[180px] flex-1"}
          />
          <select
            value={intent}
            onChange={(e) => setIntent(e.target.value as "opted_in" | "opted_out")}
            className={selectCls + " w-auto"}
          >
            <option value="opted_in">Opted in</option>
            <option value="opted_out">Opted out</option>
          </select>
        </div>
        <input
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Optional note (audit trail)"
          className={textInputCls}
        />
        <SaveBar>
          <Button type="submit" disabled={busy || !phone}>
            {busy ? "Recording…" : "Record"}
          </Button>
          {msg && <span className="text-[12px] text-emerald-600 dark:text-emerald-400">{msg}</span>}
          {err && <ErrText>{err}</ErrText>}
        </SaveBar>
      </form>
    </PanelCard>
  );
}

function Counter({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone: "emerald" | "zinc" | "red";
}) {
  const tones = {
    emerald: "text-emerald-700 dark:text-emerald-300",
    zinc: "text-zinc-700 dark:text-zinc-300",
    red: "text-red-700 dark:text-red-300",
  } as const;
  return (
    <div className="rounded-lg border border-zinc-200 px-3 py-2.5 dark:border-zinc-800/80">
      <p className="text-[11px] uppercase tracking-wider text-zinc-500">{label}</p>
      <p className={`mt-0.5 text-xl font-semibold ${tones[tone]}`}>{value}</p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Integrations
// ---------------------------------------------------------------------------

function IntegrationsPanel() {
  const [integrations, setIntegrations] = useState<{ google: boolean } | null>(null);
  const [err, setErr] = useState<string | null>(null);

  async function refresh() {
    try {
      const res = await api.agentflow.listIntegrations();
      setIntegrations({
        google: res.integrations.some((i) => i.provider === "google-calendar"),
      });
    } catch (e) {
      setErr(e instanceof ApiClientError ? e.message : "Failed to load integrations");
    }
  }

  useEffect(() => {
    void refresh();
  }, []);

  async function disconnect() {
    try {
      await api.agentflow.disconnectCalendar();
      await refresh();
    } catch (e) {
      setErr(e instanceof ApiClientError ? e.message : "Disconnect failed");
    }
  }

  return (
    <PanelCard title="Integrations" subtitle="External services AgentFlow can call out to.">
      {err && <p className="mb-3 text-[12px] text-red-600 dark:text-red-400">{err}</p>}
      <SubCard title="Google Calendar" configured={integrations?.google}>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="max-w-md text-[12px] leading-relaxed text-zinc-500">
            Real bookings via the <code className="font-mono text-[11px]">book_appointment</code>{" "}
            tool. Without it the agent still captures details into Pending appointments.
          </p>
          {integrations?.google ? (
            <Button variant="ghost-danger" onClick={() => void disconnect()}>
              Disconnect
            </Button>
          ) : (
            <a
              href={api.agentflow.calendarAuthorizeUrl()}
              className="inline-flex h-9 items-center rounded-md bg-gradient-to-br from-brand-500 via-brand-600 to-brand-700 px-4 text-[13.5px] font-medium text-white shadow-glow transition hover:brightness-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-brand-500 focus-visible:ring-offset-white dark:focus-visible:ring-offset-zinc-950"
            >
              Connect Google Calendar
            </a>
          )}
        </div>
      </SubCard>
    </PanelCard>
  );
}

// ---------------------------------------------------------------------------
// Icons (18px stroke, hand-rolled to avoid an icon dep)
// ---------------------------------------------------------------------------

function handoffIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" aria-hidden="true">
      <path
        d="M3 8h13l-3-3M21 16H8l3 3"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function tagIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" aria-hidden="true">
      <path
        d="M3 12V5a2 2 0 0 1 2-2h7l9 9-9 9-9-9Z"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
      <circle cx="7.5" cy="7.5" r="1.3" fill="currentColor" />
    </svg>
  );
}

function chipIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" aria-hidden="true">
      <rect x="6" y="6" width="12" height="12" rx="2" stroke="currentColor" strokeWidth="1.6" />
      <rect x="9.5" y="9.5" width="5" height="5" rx="1" stroke="currentColor" strokeWidth="1.4" />
      <path
        d="M9 3v3M15 3v3M9 18v3M15 18v3M3 9h3M3 15h3M18 9h3M18 15h3"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

function webhookIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" aria-hidden="true">
      <circle cx="12" cy="8" r="3" stroke="currentColor" strokeWidth="1.6" />
      <circle cx="6" cy="17" r="3" stroke="currentColor" strokeWidth="1.6" />
      <circle cx="18" cy="17" r="3" stroke="currentColor" strokeWidth="1.6" />
      <path
        d="m10.5 10.6-3 3.8M13.5 10.6l3 3.8M8.7 17h6.6"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

function shieldIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" aria-hidden="true">
      <path
        d="M12 3 5 6v5c0 4.4 3 7.6 7 9 4-1.4 7-4.6 7-9V6l-7-3Z"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
      <path
        d="m9 12 2 2 4-4"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function plugIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" aria-hidden="true">
      <path
        d="M9 3v5M15 3v5M7 8h10v3a5 5 0 0 1-10 0V8ZM12 16v5"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
    </svg>
  );
}
